import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import MatchReportModal from './MatchReportModal';

const PREVIEW_LIMIT = 5;
const FALLBACK_LOGO = '/assets/images/no_image.png';

const TeamLogo = ({ src, alt, sizeClass = 'w-10 h-10' }) => (
  <div
    className={`${sizeClass} rounded-full overflow-hidden bg-muted shrink-0 border border-border flex items-center justify-center`}
  >
    <Image
      src={src || FALLBACK_LOGO}
      alt={alt || 'Team logo'}
      className="w-full h-full object-contain p-0.5"
    />
  </div>
);

const RecentResults = ({ results, competitionType = 'league' }) => {
  const [showAll, setShowAll] = useState(false);
  const [reportResult, setReportResult] = useState(null);

  const visibleResults = useMemo(() => {
    if (!results?.length) return [];
    if (showAll || results.length <= PREVIEW_LIMIT) return results;
    return results.slice(0, PREVIEW_LIMIT);
  }, [results, showAll]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const venueLines = (venue) => {
    if (!venue) return ['TBD'];
    return String(venue)
      .split(/[\n,|]+/)
      .map((part) => part.trim())
      .filter(Boolean);
  };

  const getWinnerStyle = (homeScore, awayScore, isHome) => {
    const isWinner = isHome ? homeScore > awayScore : awayScore > homeScore;
    return isWinner ? 'text-foreground font-bold' : 'text-muted-foreground';
  };

  const getMatchHighlight = (highlight) => {
    const highlights = {
      'high-scoring': { icon: 'Zap', color: 'text-warning', label: 'High Scoring' },
      overtime: { icon: 'Clock', color: 'text-error', label: 'Overtime' },
      upset: { icon: 'TrendingUp', color: 'text-success', label: 'Upset Win' },
      close: { icon: 'Target', color: 'text-accent', label: 'Close Game' },
    };

    return highlights?.[highlight] || null;
  };

  const subtitle = () => {
    if (!results?.length) return 'No completed matches yet';
    if (showAll || results.length <= PREVIEW_LIMIT) {
      return `Showing ${results.length} completed match${results.length !== 1 ? 'es' : ''}`;
    }
    return `Showing ${PREVIEW_LIMIT} of ${results.length} completed matches`;
  };

  const DateVenueBlock = ({ result, centered = true }) => {
    const time = formatTime(result?.date);
    const lines = venueLines(result?.venue);
    return (
      <div className={`flex flex-col min-w-0 ${centered ? 'items-center text-center w-28' : 'items-start'}`}>
        <span className="text-sm font-medium text-foreground">{formatDate(result?.date)}</span>
        {time && <span className="text-xs font-medium text-primary mt-0.5">{time}</span>}
        {lines.map((line, index) => (
          <span key={`${index}-${line}`} className="text-xs text-muted-foreground">
            {line}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border card-shadow">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Recent Results</h2>
            <p className="text-sm text-muted-foreground mt-1">{subtitle()}</p>
          </div>
          {results?.length > PREVIEW_LIMIT && (
            <Button
              variant="outline"
              size="sm"
              iconName={showAll ? 'ChevronUp' : 'BarChart3'}
              iconPosition="left"
              onClick={() => setShowAll((prev) => !prev)}
            >
              {showAll ? 'Show Less' : 'All Results'}
            </Button>
          )}
        </div>
      </div>

      {/* Desktop List View */}
      <div className="hidden md:block">
        {!results || results.length === 0 ? (
          <div className="p-12 text-center">
            <Icon name="CalendarX" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No completed matches to display yet.</p>
            <p className="text-sm text-muted-foreground mt-2">
              Results will appear here once matches are completed.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {visibleResults.map((result) => {
              const highlight = getMatchHighlight(result?.highlight);

              return (
                <div key={result?.id} className="p-6 hover:bg-muted/30 transition-colors">
                  <div className="flex items-center justify-between">
                    <DateVenueBlock result={result} />

                    <div className="flex-1 flex items-center justify-center space-x-8 mx-8">
                      <div className="flex items-center space-x-3 flex-1 justify-end">
                        <div className="text-right">
                          <p
                            className={`font-medium ${getWinnerStyle(result?.homeScore, result?.awayScore, true)}`}
                          >
                            {result?.homeTeam?.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {result?.homeTeam?.shortName}
                          </p>
                        </div>
                        <TeamLogo
                          src={result?.homeTeam?.logo}
                          alt={result?.homeTeam?.logoAlt || `${result?.homeTeam?.name} logo`}
                        />
                      </div>

                      <div className="flex flex-col items-center">
                        <div className="flex items-center space-x-2">
                          <span
                            className={`text-xl font-bold ${getWinnerStyle(result?.homeScore, result?.awayScore, true)}`}
                          >
                            {result?.homeScore}
                          </span>
                          <span className="text-muted-foreground">-</span>
                          <span
                            className={`text-xl font-bold ${getWinnerStyle(result?.homeScore, result?.awayScore, false)}`}
                          >
                            {result?.awayScore}
                          </span>
                        </div>
                        {result?.overtime && (
                          <span className="text-xs text-error font-medium">OT</span>
                        )}
                      </div>

                      <div className="flex items-center space-x-3 flex-1">
                        <TeamLogo
                          src={result?.awayTeam?.logo}
                          alt={result?.awayTeam?.logoAlt || `${result?.awayTeam?.name} logo`}
                        />
                        <div>
                          <p
                            className={`font-medium ${getWinnerStyle(result?.homeScore, result?.awayScore, false)}`}
                          >
                            {result?.awayTeam?.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {result?.awayTeam?.shortName}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-end min-w-0 w-36">
                      {highlight && (
                        <div className={`flex items-center space-x-1 text-xs mb-1 ${highlight?.color}`}>
                          <Icon name={highlight?.icon} size={12} />
                          <span>{highlight?.label}</span>
                        </div>
                      )}
                      <Button
                        variant="ghost"
                        size="xs"
                        iconName="Eye"
                        iconPosition="right"
                        onClick={() => setReportResult(result)}
                      >
                        Match Report
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden p-4 space-y-4">
        {!results || results.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="CalendarX" size={32} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">No completed matches to display yet.</p>
          </div>
        ) : (
          visibleResults.map((result) => {
            const highlight = getMatchHighlight(result?.highlight);
            const time = formatTime(result?.date);

            return (
              <div key={result?.id} className="bg-muted/30 rounded-lg p-4 border border-border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center space-x-2">
                      <Icon name="Calendar" size={16} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">{formatDate(result?.date)}</span>
                    </div>
                    {time && (
                      <div className="flex items-center space-x-2 pl-6">
                        <span className="text-xs font-medium text-primary">{time}</span>
                      </div>
                    )}
                  </div>
                  {highlight && (
                    <div className={`flex items-center space-x-1 text-xs ${highlight?.color}`}>
                      <Icon name={highlight?.icon} size={12} />
                      <span>{highlight?.label}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <TeamLogo
                      src={result?.homeTeam?.logo}
                      alt={result?.homeTeam?.logoAlt}
                      sizeClass="w-12 h-12"
                    />
                    <div className="min-w-0">
                      <p
                        className={`font-medium text-sm truncate ${getWinnerStyle(result?.homeScore, result?.awayScore, true)}`}
                      >
                        {result?.homeTeam?.name}
                      </p>
                      <p className="text-xs text-muted-foreground">{result?.homeTeam?.shortName}</p>
                    </div>
                  </div>

                  <div className="flex flex-col items-center px-3 shrink-0">
                    <div className="flex items-center space-x-2">
                      <span
                        className={`text-lg font-bold ${getWinnerStyle(result?.homeScore, result?.awayScore, true)}`}
                      >
                        {result?.homeScore}
                      </span>
                      <span className="text-muted-foreground">-</span>
                      <span
                        className={`text-lg font-bold ${getWinnerStyle(result?.homeScore, result?.awayScore, false)}`}
                      >
                        {result?.awayScore}
                      </span>
                    </div>
                    {result?.overtime && (
                      <span className="text-xs text-error font-medium">OT</span>
                    )}
                  </div>

                  <div className="flex items-center space-x-3 flex-1 justify-end min-w-0">
                    <div className="text-right min-w-0">
                      <p
                        className={`font-medium text-sm truncate ${getWinnerStyle(result?.homeScore, result?.awayScore, false)}`}
                      >
                        {result?.awayTeam?.name}
                      </p>
                      <p className="text-xs text-muted-foreground">{result?.awayTeam?.shortName}</p>
                    </div>
                    <TeamLogo
                      src={result?.awayTeam?.logo}
                      alt={result?.awayTeam?.logoAlt}
                      sizeClass="w-12 h-12"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1 text-xs text-muted-foreground min-w-0">
                    <Icon name="MapPin" size={12} className="shrink-0" />
                    <span className="truncate">{result?.venue}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="xs"
                    iconName="Eye"
                    iconPosition="right"
                    onClick={() => setReportResult(result)}
                  >
                    Report
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {reportResult && (
        <MatchReportModal
          result={reportResult}
          competitionType={competitionType}
          onClose={() => setReportResult(null)}
        />
      )}
    </div>
  );
};

export default RecentResults;
